import React, { useCallback, useEffect, useState } from "react";
import TableContainer from "Common/TableContainer";
import { Loader } from "../../Common/Loader";
import { Pencil, Trash2, Plus } from "lucide-react";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer } from "react-toastify";
import { Common } from "helpers/common";
import moment from "moment";
import { AddProgram } from "./AddProgram";
import { getProgramList, deleteProgram } from "helpers/backend_helper";

function ProgramList() {
  const [programs, setPrograms] = useState<any[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<any[]>([]);
  const [eventData, setEventData] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [show, setShow] = useState<boolean>(false);
  const [programData, setProgramData] = useState<any>({});
  const uiHelper = new Common();

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  useEffect(() => {
    document.body.classList.remove('overflow-hidden');
    loadPrograms(false);
  }, []);

  async function loadPrograms(reload: boolean) {
    try {
      const response = await getProgramList();
      console.log("Full program response:", response);
      setPrograms(response.data);
      setFilteredPrograms(response.data);
    } catch (error) {
      console.error("Error loading programs:", error);
    } finally {
      setIsLoading(false);
    }
  }

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell.programId) {
      setEventData(cell);
    }
  };

  const handleDelete = async () => {
    if (eventData && eventData.programId) {
      try {
        await deleteProgram({ programId: eventData.programId });
        uiHelper.showSuccessMessage("Program deleted successfully");
        loadPrograms(true);
        setDeleteModal(false);
      } catch (error: any) {
        console.error("Error deleting program:", error);
        uiHelper.showErrorMessage("Error deleting program");
      }
    }
  };

  const handleEdit = (cell: any) => {
    setProgramData(cell);
    setShow(true);
  };

  const handleAddProgram = () => {
    setProgramData({});
    setShow(true);
  };

  const filterSearchData = (e: any) => {
    const search = e.target.value.toLowerCase();
    setSearchText(search);
    
    if (search === '') {
      setFilteredPrograms(programs);
    } else {
      const filtered = programs.filter((program: any) => 
        program.programName?.toLowerCase().includes(search) ||
        program.description?.toLowerCase().includes(search)
      );
      setFilteredPrograms(filtered);
    }
  };

  const onSave = (result: any) => {
    console.log("Save result:", result);
    loadPrograms(true);
    toggle();
  };

  const toggle = useCallback(() => {
    setShow(!show);
    if (show) {
      setProgramData({});
    }
  }, [show]);

  const columns = [
    {
      header: "Program Name",
      accessorKey: "programName",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Description",
      accessorKey: "description",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <div className="max-w-xs truncate" title={row.original.description}>
          {row.original.description || "No description"}
        </div>
      ),
    },
    {
      header: "Start Date",
      accessorKey: "startDate",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.startDate ? moment(row.original.startDate).format("MMM DD, YYYY") : '-'}</span>
      ),
    },
    {
      header: "End Date",
      accessorKey: "endDate",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.endDate ? moment(row.original.endDate).format("MMM DD, YYYY") : '-'}</span>
      ),
    },
    {
      header: "Created On",
      accessorKey: "createdOn",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.createdOn ? moment(row.original.createdOn).format("MMM DD, YYYY") : '-'}</span>
      ),
    },
    {
      header: "Created By",
      accessorKey: "createdByName",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.createdByName || '-'}</span>
      ),
    },
    {
      header: "Updated On",
      accessorKey: "updatedOn",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.updatedOn ? moment(row.original.updatedOn).format("MMM DD, YYYY") : '-'}</span>
      ),
    },
    {
      header: "Updated By",
      accessorKey: "updatedByName",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.updatedByName || '-'}</span>
      ),
    },
    {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => {
        return (
          <div className="flex gap-3">
            <button
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/20"
              onClick={() => handleEdit(row.original)}
            >
              <Pencil className="size-4" />
            </button>
            <button
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 text-slate-500 dark:text-zink-200 hover:text-red-500 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20"
              onClick={() => onClickDelete(row.original)}
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12" style={{ marginTop: 15 }}>
        <div className="xl:col-span-12">
          <div className="card" id="programTable">

            <div className="flex items-center justify-between border-y border-dashed" style={{ padding: 10 }}>
              <div className="flex items-center gap-2 shrink-0">
                <h6 className="text-15">Filter </h6>
                <input
                  type="text"
                  className="w-full form-input  focus:outline-none focus:border-none focus:ring focus:ring-custom-100"
                  placeholder="Program Name or Description"
                  value={searchText}
                  onChange={filterSearchData}
                />

                <div className="flex justify-end shrink-0">
                  <button
                    type="button"
                    className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    onClick={handleAddProgram}
                    style={{ marginLeft: "auto" }}
                  >
                    <Plus className="inline-block size-4" />{" "}
                    <span className="align-middle">Add Program</span>
                  </button>
                </div>
              </div>
            </div>


            {isLoading ? (
              <Loader />
            ) : (
              <TableContainer
                columns={columns}
                data={filteredPrograms}
                isPagination={true}
                customPageSize={15}
                divclassName="overflow-x-auto"
                tableclassName="w-full whitespace-nowrap"
                theadclassName="ltr:text-left rtl:text-right bg-custom-200 dark:bg-zink-600"
                thclassName="px-2.5 py-1.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                tdclassName="px-2.5 py-1.0 border-y border-slate-200 dark:border-zink-500"
                trclassName="even:bg-custom-50  dark:odd:bg-zink-700 dark:even:bg-zink-600"
                PaginationClassName="flex flex-col items-center gap-4 px-4 mt-3 md:flex-row"
              />
            )}

          </div>
        </div>
      </div>


      {programData ? (
        <AddProgram 
          show={show} 
          onHide={toggle} 
          data={programData} 
          onSave={onSave}
        />) : (<></>)}

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
    </React.Fragment>
  );
}

export default ProgramList;